import React, { useState } from 'react';
import { Repeat, Flag, Check, Activity } from 'lucide-react';
import { formatDate } from '../../lib/formatDate';
import * as miniLeaguesApi from '../../api/miniLeagues';

/**
 * League activity: one entry per game recreation or season end.
 *
 * This used to also list the finished game's "game-only joiners" so the manager
 * could promote them. That state no longer exists — joining a league's game by
 * its code makes you a member — so there is nobody left to promote and the block
 * is gone.
 */
function LeagueNotices({ notices, tournaments, isManager, onChanged, onViewGame }) {
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const tournamentNameFor = (miniLeagueTournamentId) => {
    const match = (tournaments || []).find(
      (t) => String(t.id) === String(miniLeagueTournamentId)
    );
    return match ? match.tournamentName : 'This league';
  };

  const run = async (key, action, successMessage) => {
    setError('');
    setNotice('');
    setBusyId(key);
    try {
      await action();
      setNotice(successMessage);
      if (onChanged) onChanged();
    } catch (err) {
      if (err.name === 'ApiError') {
        setError(err.body?.error || 'Something went wrong');
      } else {
        setError('Something went wrong');
      }
    } finally {
      setBusyId(null);
    }
  };

  const acknowledge = (n) =>
    run(`ack-${n.id}`, () => miniLeaguesApi.acknowledgeNotice(n.id), 'Marked as read.');

  if (!notices || notices.length === 0) {
    return (
      <div className="empty-state">
        <Activity size={56} />
        <h3>No Activity Yet</h3>
        <p>When a game finishes and the next one starts, it shows up here</p>
      </div>
    );
  }

  return (
    <>
      {notice && <div className="league-notice">{notice}</div>}
      {error && <div className="error-message">{error}</div>}

      <div className="notice-list">
        {notices.map((n) => {
          const seasonEnded = n.newGameId == null;

          return (
            <div key={n.id} className={n.acknowledged ? 'notice-row read' : 'notice-row'}>
              <div className="notice-main">
                <span className="notice-icon">
                  {seasonEnded ? <Flag size={16} /> : <Repeat size={16} />}
                </span>
                <div className="notice-body">
                  <span className="notice-title">
                    {tournamentNameFor(n.miniLeagueTournamentId)}
                    {seasonEnded ? ' — season finished' : ' — a new game has started'}
                  </span>
                  <span className="notice-date">{formatDate(n.createdAt)}</span>
                </div>

                <div className="notice-actions">
                  {!seasonEnded && n.newGameId && (
                    <button
                      className="view-game-btn league-tournament-btn"
                      onClick={() => onViewGame(n.newGameId)}
                    >
                      View Game
                    </button>
                  )}
                  {isManager && !n.acknowledged && (
                    <button
                      className="secondary-btn notice-ack-btn"
                      onClick={() => acknowledge(n)}
                      disabled={busyId === `ack-${n.id}`}
                    >
                      <Check size={16} />
                      Mark read
                    </button>
                  )}
                </div>
              </div>

            </div>
          );
        })}
      </div>
    </>
  );
}

export default LeagueNotices;
